/**
 * Web push (PWA) via Firebase Cloud Messaging. The native iOS/Android app uses the
 * Capacitor flow in native.ts instead; this is the browser path. Everything is
 * best-effort and gated behind a user gesture, push must never break reading.
 */
import { getApp, getApps, initializeApp, type FirebaseApp } from "firebase/app";
import {
  getMessaging,
  getToken,
  isSupported,
  onMessage,
  type Messaging,
} from "firebase/messaging";
import { getIdToken } from "./auth";
import { anonId } from "./engagement";
import { firebaseConfig, vapidKey } from "./firebaseConfig";

const ENGAGEMENT_URL =
  (import.meta.env.VITE_ENGAGEMENT_URL as string | undefined) ??
  "https://insnip-engagement.insnip-app.workers.dev";

// Registered at its own scope so it never collides with the PWA worker (sw.js at "/").
const FCM_SW_PATH = "/firebase-messaging-sw.js";
const FCM_SW_SCOPE = "/firebase-push/";
const PUSH_ON_KEY = "insnip.pushOn";

export type PushPermission = "default" | "granted" | "denied" | "unsupported";

let messaging: Messaging | null = null;

function firebaseApp(): FirebaseApp {
  return getApps().length ? getApp() : initializeApp(firebaseConfig);
}

export async function pushSupported(): Promise<boolean> {
  try {
    return (
      typeof window !== "undefined" &&
      "Notification" in window &&
      "serviceWorker" in navigator &&
      (await isSupported())
    );
  } catch {
    return false;
  }
}

export function pushPermission(): PushPermission {
  if (typeof window === "undefined" || !("Notification" in window)) return "unsupported";
  return Notification.permission as PushPermission;
}

/** True if the reader previously turned push on (drives the toggle's default state). */
export function pushEnabledLocally(): boolean {
  try {
    return localStorage.getItem(PUSH_ON_KEY) === "1" && pushPermission() === "granted";
  } catch {
    return false;
  }
}

async function ensureMessaging(): Promise<Messaging | null> {
  if (!(await pushSupported())) return null;
  if (!messaging) messaging = getMessaging(firebaseApp());
  return messaging;
}

async function registerToken(token: string): Promise<void> {
  try {
    // A signed-in reader proves the account uid with their Firebase ID token (the worker
    // requires it for an account uid); signed out, or if the refresh fails, the device id
    // goes on its own exactly as before.
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    try {
      const idToken = await getIdToken();
      if (idToken) headers.Authorization = `Bearer ${idToken}`;
    } catch {
      /* no Firebase session: register unauthenticated */
    }
    await fetch(`${ENGAGEMENT_URL}/push-token`, {
      method: "POST",
      headers,
      // tzOffset: minutes east of UTC (getTimezoneOffset is minutes behind, so negate) so the
      // worker fires notifications in the reader's own local time / quiet hours.
      body: JSON.stringify({ u: anonId(), token, platform: "web", tzOffset: -new Date().getTimezoneOffset() }),
      keepalive: true,
    });
  } catch {
    /* best-effort */
  }
}

async function obtainToken(messagingInstance: Messaging): Promise<string | null> {
  const registration = await navigator.serviceWorker.register(FCM_SW_PATH, {
    scope: FCM_SW_SCOPE,
  });
  const token = await getToken(messagingInstance, {
    vapidKey,
    serviceWorkerRegistration: registration,
  });
  return token || null;
}

/**
 * User-gesture entry point: prompt for permission, get an FCM token, register it.
 * Returns true once the reader is subscribed. Call from a click handler.
 */
export async function enableWebPush(): Promise<boolean> {
  const m = await ensureMessaging();
  if (!m) return false;
  try {
    const permission = await Notification.requestPermission();
    if (permission !== "granted") return false;
    const token = await obtainToken(m);
    if (!token) return false;
    await registerToken(token);
    try {
      localStorage.setItem(PUSH_ON_KEY, "1");
    } catch {
      /* ignore */
    }
    return true;
  } catch {
    return false;
  }
}

/** Reader turned push off in the UI, we stop refreshing the token (best-effort). */
export function disableWebPushLocally(): void {
  try {
    localStorage.removeItem(PUSH_ON_KEY);
  } catch {
    /* ignore */
  }
}

/** If already granted + opted in, silently refresh the token on load to keep it live. */
export async function refreshWebPushToken(): Promise<void> {
  if (!pushEnabledLocally()) return;
  const m = await ensureMessaging();
  if (!m) return;
  try {
    const token = await obtainToken(m);
    if (token) await registerToken(token);
  } catch {
    /* ignore */
  }
}

/** In-app toast hook for a push that arrives while the tab is focused. */
export async function onForegroundPush(
  cb: (title: string, body: string, url?: string) => void,
): Promise<void> {
  const m = await ensureMessaging();
  if (!m) return;
  onMessage(m, (payload) => {
    const data = payload.data ?? {};
    const title = data.title ?? payload.notification?.title;
    if (title) cb(title, data.body ?? payload.notification?.body ?? "", data.url);
  });
}
